"use client";

import { c as _c } from "react/compiler-runtime";
import { getCookie, setCookie } from "cookies-next/client";
import { format, isBefore } from "date-fns";
import React, { useEffect, useState } from "react";
import DateSelector from "src/components/dateselector/DateSelector";
import { ProgramDatesContext, useLocationContext } from "src/lib/contexts/ContextTypes";
import { parseProgramHash, updateProgramHash } from "src/lib/programHash";

/**
 * Chooses the date to start with. The hash wins over the cookie, a date in the past gives today.
 * Pure, so it can be tested without a window or cookies.
 */
export function getInitialDates(hash: string, cookie: string | undefined, today = new Date()): {
  date: Date;
  showingAllDates: boolean;
} {
  const fromHash = parseProgramHash(hash).date;
  // no date in the hash - fall back to the date selected on the previous page
  const chosen = fromHash ?? (cookie ? parseProgramHash(cookie).date : null);
  if (chosen === "all") {
    return {
      date: today,
      showingAllDates: true
    };
  }
  return {
    date: chosen && !isBefore(chosen, today) ? chosen : today,
    showingAllDates: false
  };
}
function readInitialDates() {
  if (typeof window === "undefined") {
    return {
      date: new Date(),
      showingAllDates: false
    };
  }
  return getInitialDates(window.location.hash, getCookie("tmpSelectedDate"));
}

//#endregion

//#region [Props]
type ProgramDatesWrapperProps = {
  children: React.ReactNode;
};
//#endregion

//#region [Component]
export default function ProgramDatesWrapper(t0) {
  const $ = _c(18);
  const {
    children
  } = t0;
  const [initialDates] = useState(readInitialDates);
  const [selectedDate, setSelectedDate] = useState(initialDates.date);
  const [isShowingAllDates, setIsShowingAllDates] = useState(initialDates.showingAllDates);
  const [showSelectDateDialog, setShowSelectDateDialog] = useState(false);
  const [availableDates, setAvailableDates] = useState(null);
  const {
    showAllDates,
    location,
    isReady
  } = useLocationContext();
  const showingAllDates = isShowingAllDates && (!isReady || !!showAllDates);
  let t1;
  if ($[0] !== isReady || $[1] !== location) {
    t1 = isReady ? (location ?? []).join("|") : null;
    $[0] = isReady;
    $[1] = location;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const locationKey = t1;
  const [prevLocationKey, setPrevLocationKey] = useState(locationKey);
  let t2;
  let t3;
  if ($[3] !== selectedDate || $[4] !== showingAllDates) {
    t2 = () => {
      const date = showingAllDates ? "all" : selectedDate;
      updateProgramHash({
        date
      });
      setCookie("tmpSelectedDate", date === "all" ? date : format(date, "dd.MM.yyyy"));
    };
    t3 = [selectedDate, showingAllDates];
    $[3] = selectedDate;
    $[4] = showingAllDates;
    $[5] = t2;
    $[6] = t3;
  } else {
    t2 = $[5];
    t3 = $[6];
  }
  useEffect(t2, t3);
  if (locationKey !== prevLocationKey) {
    setPrevLocationKey(locationKey);
    if (prevLocationKey !== null) {
      setSelectedDate(new Date());
      setIsShowingAllDates(!!showAllDates);
    }
  }
  let t4;
  if ($[7] !== availableDates || $[8] !== selectedDate || $[9] !== showSelectDateDialog || $[10] !== showingAllDates) {
    t4 = {
      selectedDate,
      showingAllDates,
      setIsShowingAllDates,
      setSelectedDate,
      showSelectDateDialog,
      setShowSelectDateDialog,
      setAvailableDates,
      availableDates
    };
    $[7] = availableDates;
    $[8] = selectedDate;
    $[9] = showSelectDateDialog;
    $[10] = showingAllDates;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== showSelectDateDialog) {
    t5 = showSelectDateDialog && <DateSelector />;
    $[12] = showSelectDateDialog;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== children || $[15] !== t4 || $[16] !== t5) {
    t6 = <ProgramDatesContext.Provider value={t4}>{children}{t5}</ProgramDatesContext.Provider>;
    $[14] = children;
    $[15] = t4;
    $[16] = t5;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  return t6;
}
//#endregion